import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { ARAgingEntry } from '../../types/franchise';
import { formatCurrency } from '../../utils/formatters';

interface ARAgingTableProps {
  data: ARAgingEntry[];
}

const getBucketConfig = (bucket: string) => {
  switch (bucket) {
    case '90+':
      return { color: '#D32F2F', bg: '#FFEBEE', label: '90+ Days' };
    case '60+':
      return { color: '#E65100', bg: '#FFF3E0', label: '60+ Days' };
    case '30-60':
      return { color: '#F57C00', bg: '#FFF8E1', label: '30-60 Days' };
    default:
      return { color: '#9E9E9E', bg: '#F5F5F5', label: bucket };
  }
};

export const ARAgingTable: React.FC<ARAgingTableProps> = ({ data }) => {
  // Sort by days overdue descending
  const sorted = [...data].sort((a, b) => b.daysOverdue - a.daysOverdue);
  const totalOutstanding = data.reduce((sum, entry) => sum + entry.amountDue, 0);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ fontSize: 20, color: '#F57C00' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
              AR Aging (Royalty/Tech Fees)
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#D32F2F' }}>
              {formatCurrency(totalOutstanding, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Total outstanding
            </Typography>
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Franchisee
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Type
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Amount
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Status
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((entry, index) => {
                const config = getBucketConfig(entry.bucket);
                return (
                  <TableRow
                    key={index}
                    sx={{
                      '&:hover': { bgcolor: `${config.bg}80` },
                      '& td': { borderBottom: '1px solid #f0f0f0' },
                    }}
                  >
                    <TableCell>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                        {entry.franchisee}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                        {entry.type}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: config.color }}>
                        {formatCurrency(entry.amountDue, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={config.label}
                        size="small"
                        sx={{
                          bgcolor: config.bg,
                          color: config.color,
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          height: 24,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};
